"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

export default function DocsBlocksDatePicker() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Date Picker
            </Text>
            <Badge variant="secondary">Block</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            Date selection components for single dates and date ranges. Built with
            react-day-picker for flexible date handling.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
        header={<CardTitle>Features</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Single date selection</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Date range selection</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Preset date ranges</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Fiscal/calendar year support</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Form integration</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Keyboard navigation</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Single Date Usage */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Single Date Picker</CardTitle>
            <CardDescription>Basic usage for selecting a single date</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`import { DatePickerBlock } from "@/registry/new-york/blocks/date-picker/date-picker";

const [date, setDate] = useState<Date | null>(null);

<DatePickerBlock
  label="Select date"
  value={date}
  onChange={setDate}
  placeholder="Pick a date"
/>`}</pre>
        </div>
      </SectionCard>
      </Section>

      {/* Date Range Usage */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Date Range Picker</CardTitle>
            <CardDescription>Select a start and end date with presets</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`import { DateRangePickerBlock } from "@/registry/new-york/blocks/date-range-picker/date-range-picker";
import type { DateRangeValue } from "@/lib/date-range";

const [range, setRange] = useState<DateRangeValue>({
  kind: "CURRENT",
  value: "current_month",
  yearType: "calendar_year",
});

<DateRangePickerBlock
  label="Select range"
  value={range}
  onChange={setRange}
/>`}</pre>
        </div>
      </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
        header={<CardTitle>Import</CardTitle>}
        contentClassName="p-6 space-y-2"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-2">
          <code className="block">{`import { DatePickerBlock } from "@/registry/new-york/blocks/date-picker/date-picker";`}</code>
          <code className="block">{`import { DateRangePickerBlock } from "@/registry/new-york/blocks/date-range-picker/date-range-picker";`}</code>
          <code className="block">{`import type { DateRangeValue } from "@/lib/date-range";`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Props - DatePicker */}
      <Section>
        <SectionCard
        header={<CardTitle>DatePickerBlock Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">value</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">Date | null</td>
              <td className="py-2 text-muted-foreground">Controlled date value</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">defaultValue</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">Date | null</td>
              <td className="py-2 text-muted-foreground">Uncontrolled initial value</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">onChange</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">(date: Date | null) =&gt; void</td>
              <td className="py-2 text-muted-foreground">Called when date changes</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">label</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 text-muted-foreground">Field label</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">helpText</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 text-muted-foreground">Helper text below field</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">placeholder</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
              <td className="py-2 text-muted-foreground">Placeholder text</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">disabled</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 text-muted-foreground">Disable the picker</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Props - DateRangePicker */}
      <Section>
        <SectionCard
        header={<CardTitle>DateRangePickerBlock Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">value</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">DateRangeValue</td>
              <td className="py-2 text-muted-foreground">Controlled range value</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">defaultValue</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">DateRangeValue</td>
              <td className="py-2 text-muted-foreground">Uncontrolled initial value</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">onChange</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">(range: DateRangeValue) =&gt; void</td>
              <td className="py-2 text-muted-foreground">Called when range changes</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">yearTypeDefault</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">&quot;fiscal_year&quot; | &quot;calendar_year&quot;</td>
              <td className="py-2 text-muted-foreground">Default year type for presets</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">fiscalYearStartMonth</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">number</td>
              <td className="py-2 text-muted-foreground">Fiscal year start month (1-12)</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">label</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 text-muted-foreground">Field label</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* DateRangeValue Type */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>DateRangeValue Type</CardTitle>
            <CardDescription>Structure for range picker values</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`type DateRangeValue = {
  kind: "ROLLING" | "CURRENT" | "PREVIOUS" | "NEXT" | "CUSTOM";
  value?: string;      // Preset identifier like "past_week"
  yearType: "fiscal_year" | "calendar_year";
  startDate?: Date;    // For custom ranges
  endDate?: Date;      // For custom ranges
};

// Preset options by kind:
// ROLLING: "past_week" | "past_month" | "past_quarter" | "past_year"
// CURRENT: "current_week" | "current_month" | "current_quarter" | "current_year"
// PREVIOUS: "yesterday" | "previous_week" | "previous_month" | "previous_quarter" | "previous_year"
// NEXT: "next_day" | "next_week" | "next_month" | "next_quarter" | "next_year"
// CUSTOM: "custom_period" (uses startDate and endDate)`}</pre>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
